import { Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BankBeneficiaryService, GetBankBeneficiariesDto } from '../services/bank-beneficiary.service';
import { BankBeneficiary, Pagination } from './types';
import { BankAccountsStore } from './bank-accounts.store';

@Injectable({
  providedIn: 'root'
})
export class BankBeneficiariesStore {
  loading = signal(false);
  loadingDescription = signal('');
  bankBeneficiaries = signal<BankBeneficiary[]>([]);
  pagination = signal<Pagination>(null);
  paginationInput = signal<{ page: number; limit: number }>({ page: 1, limit: 50 });
  currentFilter = signal<GetBankBeneficiariesDto | null>(null);

  constructor(
    private bankBeneficiaryService: BankBeneficiaryService,
		private matSnackBar: MatSnackBar,
		private bankAccountsStore:BankAccountsStore
  ) {}

	async loadBankBeneficiaries(filter?: GetBankBeneficiariesDto) {
		if (!filter) {
			filter = { accountNumber: this.bankAccountsStore.selectedBankAccountId() } ;
		}
    // Merge with existing filter
    const mergedFilter = { 
      ...this.currentFilter(), 
      ...filter
		};
		
		if (mergedFilter.accountNumber) {
			mergedFilter.accountNumber = this.bankAccountsStore.selectedBankAccountId();
		}
    
    // Store the filter for future use
    this.currentFilter.set({ ...this.currentFilter(), ...filter });

    this.loading.set(true);
    try {
      const response = await this.bankBeneficiaryService.getBankBeneficiaries(
        this.paginationInput(),
        mergedFilter
      );
      if (response.data?.elements) {
        const { elements, pagination } = response.data;

        // Only append if we're loading more pages (page > 1), otherwise replace
        if (this.paginationInput().page > 1 && this.bankBeneficiaries().length > 0) {
          this.bankBeneficiaries.set([...this.bankBeneficiaries(), ...elements]);
        } else {
          this.bankBeneficiaries.set(elements as BankBeneficiary[]);
        }
        this.pagination.set(pagination);

        return this.bankBeneficiaries();
      } else if (response.error) {
        this.showLoadError(response.error?.message);
      }
    } catch (error) {
      this.showLoadError();
    } finally {
      this.loading.set(false);
    }
    return null;
  }

  reloadBankBeneficiaries() {
    this.paginationInput.set({ page: 1, limit: 50 });
    this.bankBeneficiaries.set([]);
    this.loadBankBeneficiaries(this.currentFilter());
  }

  loadMore() {
    if (this.pagination()?.hasNextPage) {
      this.paginationInput.update((current) => ({
        ...current,
        page: current.page + 1
      }));
      this.loadBankBeneficiaries(this.currentFilter());
    }
  }

  showLoadError(message?: string) {
    this.matSnackBar.open(message || 'Error al cargar beneficiarios bancarios', 'Cerrar', {
      duration: 3000,
      panelClass: 'mat-snack-error'
    });
  }

  /**
   * Clears specific filter field and reloads bank beneficiaries
   */
  clearFilterField(fieldName: keyof GetBankBeneficiariesDto) {
    const currentFilterValue = this.currentFilter();
    const newFilter = { ...currentFilterValue };
    delete newFilter[fieldName];
    this.currentFilter.set(newFilter);
    this.reloadBankBeneficiaries();
  }

  /**
   * Clears all bank beneficiaries data from the store
   * Used when a user logs out to ensure no data remains in memory
   */
  clearStore() {
    this.loading.set(false);
    this.loadingDescription.set('');
    this.bankBeneficiaries.set([]);
		this.currentFilter.set(null);
  }

  /**
   * Adds a new bank beneficiary to the store
   */
  addBankBeneficiary(beneficiary: BankBeneficiary) {
    this.bankBeneficiaries.update(current => [beneficiary, ...current]);
  }

  /**
   * Removes a bank beneficiary from the store
   */
  removeBankBeneficiary(beneficiaryId: string) {
    this.bankBeneficiaries.update(current => 
      current.filter(b => b._id !== beneficiaryId)
    );
  }
}
